import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { PrintersStatus } from 'src/app/models/printing.model';
import { PrintService } from 'src/app/common/services/print.service';
import { takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import {
  BACK_BUTTON,
  PRINTER_ICON,
  RETRY_ICON,
  PRINTERS_URL,
  PRINTER_STEP,
  PRINTER_STEP_1,
  PRINTER_STEP_2,
  PRINTER_STEP_3,
  PRINTER_STEP_4,
  WIFI_STEP,
  WIFI_STEP_1,
  ROTATING_ARROW,
  CLOSE_PRINTER,
  PRINTER_VIDEO_1,
  PRINTER_VIDEO_2,
  ACTIVATED_PRINTER,
  DEACTIVATED_PRINTER
} from 'src/defaultImages.js';
import { TranslateService } from '@ngx-translate/core';
import { ImagePreloaderService } from 'src/app/common/services/image-preload.service';
import { PrinterNameEnum, PrinterStatus } from '../enums/printer.enum';
import { LocalStorageService } from 'src/app/common/services/localStorage.service';
import { PrinterStatusService } from '../../services/printer-status.service';

const RETRY_TIMEOUT = 10000;
@Component({
  selector: 'app-all-printer-modal',
  templateUrl: './all-printer-modal.component.html',
  styleUrls: ['./all-printer-modal.component.css'],
})
export class AllPrinterModalComponent implements OnInit, OnDestroy {
  images = {
    BACK_BUTTON: BACK_BUTTON,
    PRINTER_ICON: PRINTER_ICON,
    RETRY_ICON: RETRY_ICON,
    PRINTERS_URL: PRINTERS_URL,
    PRINTER_STEP: PRINTER_STEP,
    PRINTER_STEP_1: PRINTER_STEP_1,
    PRINTER_STEP_2: PRINTER_STEP_2,
    PRINTER_STEP_3: PRINTER_STEP_3,
    PRINTER_STEP_4: PRINTER_STEP_4,
    WIFI_STEP: WIFI_STEP,
    WIFI_STEP_1: WIFI_STEP_1,
    ROTATING_ARROW: ROTATING_ARROW,
    CLOSE_PRINTER: CLOSE_PRINTER,
    PRINTER_VIDEO_1: PRINTER_VIDEO_1,
    PRINTER_VIDEO_2: PRINTER_VIDEO_2,
    ACTIVATED_PRINTER : ACTIVATED_PRINTER,
    DEACTIVATED_PRINTER: DEACTIVATED_PRINTER
  };
  @Input() allPrintersList: PrintersStatus[] = [];
  @Input() allPrintersActive: boolean = false;
  @Input() allKitchenCounter: any[] = [];
  printerNameEnum = PrinterNameEnum;
  ngUnsubscribe = new Subject();
  sortTimeoutRef: ReturnType<typeof setTimeout>;
  allPrintersDisconneted: boolean = false;
  isViewStep: boolean = false;
  subscription = new Subscription();
  translationText: any;
  // Object to store the collapse states
  isCollapsed: { [key: string]: boolean } = {
    printerDisconnected: true,
    wifiSettings: false,
  };
  printerDisconnectedCollapsedContent = [
    {
      text: 'A red printer icon on top right of Order Dashboard indicates one or more printers to be disconnected',
      imgUrl: this.images.PRINTER_STEP_1,
      videoUrl: '',
    },
    {
      text: 'Tap the printer icon to identify the disconnected printer, and press the retry button to get its latest connection status.',
      imgUrl: this.images.PRINTER_STEP_2,
      videoUrl: '',
    },
    {
      text: 'If printer still shows as disconnected, restart your printer & wait for print to happen.',
      imgUrl: this.images.PRINTER_STEP_3,
      videoUrl: this.images.PRINTER_VIDEO_1,
    },
    {
      text: 'You can confirm printer is connected to EasyEat POS by doing a test print from App Settings. The printer icon will change to green in this case.',
      imgUrl: this.images.PRINTER_STEP_4,
      videoUrl: this.images.PRINTER_VIDEO_2,
    },
  ];

  wifiCollapsedContent = [
    {
      text: 'Check if your device is connected to the configured network (Wi-Fi dedicated to EasyEat POS). If the device is disconnected, connect it back to the configured network.',
      imgUrl: this.images.WIFI_STEP_1,
      videoUrl: '',
    },
    {
      text: 'You may receive an error message if too many devices are connected to your configured network.',
      imgUrl: '',
      videoUrl: '',
    },
    {
      text: 'Disconnect the extra devices & make sure only EasyEat POS and printers are connected to the network for printing to work.',
      imgUrl: '',
      videoUrl: '',
    },
  ];
  isLoading: boolean = true;
  printerStatusEnum = PrinterStatus;

  constructor(
    private _printService: PrintService,
    private _printStatusService: PrinterStatusService,
    private _cdr: ChangeDetectorRef,
    private _translateService: TranslateService,
    private _imagePreloaderService: ImagePreloaderService,
    private _localStorage: LocalStorageService
  ) {
    this._translateService.use(this._localStorage.get('language') || 'en_US');
    this.allPrintersDisconneted =
      this._printStatusService.checkAllPrintersDisconnected();
  }

  ngOnInit() {
    this.setTranslations();
    this.preloadImages();
    this.subscribePrinterListData();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  setTranslations() {
    this.subscription.add(
      this._translateService
        .get(['PRINTER_STATUS'])
        .subscribe((translation) => {
          this.translationText = translation.PRINTER_STATUS;
          this.updateTranslations();
        })
    );
  }

  preloadImages() {
    const imageUrlsToPreload: string[] = this.generateImageUrlsFromImagesObject(
      this.images
    );
    this._imagePreloaderService
      .preloadImages(imageUrlsToPreload)
      .then(() => {
        // All images are preloaded and ready to use
        this.isLoading = false;
      })
      .catch((error) => {
        this.isLoading = false;
      });
  }

  subscribePrinterListData() {
    this._printService
      .getAllPrinterListData()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data) => {
        const tempPrintersList =
          this._printStatusService.combinePrintersList(data);
        this.allPrintersList = [...tempPrintersList];
        this.allPrintersActive =
          !this._printStatusService.checkAllPrintersStatus();
        this.allPrintersDisconneted =
          this._printStatusService.checkAllPrintersDisconnected();
      });
  }

  generateImageUrlsFromImagesObject(imageObject: {
    [key: string]: string;
  }): string[] {
    // Extracts all the image URLs from the images object and returns them as an array.
    return Object.values(imageObject);
  }

  retryPrinterConnection(printer: PrintersStatus) {
    const payload = {
      action: 'printer_status',
      ip: printer.ip,
    };
    printer['isRetrying'] = true;
    !printer['retryTimeoutRef'] &&
      (printer['retryTimeoutRef'] = setTimeout(() => {
        printer['isRetrying'] = false;
        printer['retryTimeoutRef'] = null;
      }, RETRY_TIMEOUT));
    this._printService.sendPrintStatusToRN(payload);
  }

  splitIP(ip) {
    return ip.includes(':')  ? ip.split(':')[0] : ip;
  }

  isBluetoothPrinter(inputIpString: string): boolean {
    // Check if the input string matches the pattern of a Bluetooth address
    const bluetoothAddressPattern = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
    if (bluetoothAddressPattern.test(inputIpString)) {
        return true;
    }
    return false;
}

  sortPrinterList() {
    this.allPrintersList.sort(
      (a: any, b: any) => a.isConnected - b.isConnected
    );
    this._cdr.detectChanges();
    clearTimeout(this.sortTimeoutRef);
    this.sortTimeoutRef = null;
  }

  trackPrinter(index, printer) {
    return printer ? printer.id : undefined;
  }

  updateTranslations() {
    this.printerDisconnectedCollapsedContent = [
      {
        text: this.translationText.A_RED_PRINTER_ICON,
        imgUrl: this.images.PRINTER_STEP_1,
        videoUrl: '',
      },
      {
        text: this.translationText.TAP_THE_PRINTER_ICON,
        imgUrl: this.images.PRINTER_STEP_2,
        videoUrl: '',
      },
      {
        text: this.translationText.IF_PRINTER_STILL_SHOWS,
        imgUrl: this.images.PRINTER_STEP_3,
        videoUrl: this.images.PRINTER_VIDEO_1,
      },
      {
        text: this.translationText.YOU_CAN_CONFIRM_PRINTER,
        imgUrl: this.images.PRINTER_STEP_4,
        videoUrl: this.images.PRINTER_VIDEO_2,
      },
    ];

    this.wifiCollapsedContent = [
      {
        text: this.translationText.CHECK_IF_YOUR_DEVICE_IS_CONNECTED,
        imgUrl: this.images.WIFI_STEP_1,
        videoUrl: '',
      },
      {
        text: this.translationText.YOU_MAY_RECEIVE_AN_ERROR,
        imgUrl: '',
        videoUrl: '',
      },
      {
        text: this.translationText.DISCONNECT_THE_EXTRA_DEVICES,
        imgUrl: '',
        videoUrl: '',
      },
    ];
  }

  switchToViewStep() {
    this.isViewStep = !this.isViewStep;
    const closeIcon = document.getElementById('printer_cancel');
    if (!closeIcon) return;
    if (this.isViewStep) {
      closeIcon.style.display = 'none';
    } else {
      closeIcon.style.display = 'block';
      closeIcon.style.marginTop = '10px';
    }
  }

  // Function to toggle the collapse state based on the action
  performCollapseAction(action: string): void {
    switch (action) {
      case 'disconnect':
        // Toggle the printerDisconnected collapse state
        this.isCollapsed.printerDisconnected =
          !this.isCollapsed.printerDisconnected;
        this.adjustOtherCollapses('printerDisconnected');
        break;
      case 'wifi':
        // Toggle the wifiSettings collapse state
        this.isCollapsed.wifiSettings = !this.isCollapsed.wifiSettings;
        this.adjustOtherCollapses('wifiSettings');
        break;
      default:
        // Set the initial collapse state as default
        this.isCollapsed = {
          printerDisconnected: true,
          wifiSettings: false,
        };
        break;
    }
  }

  trackByPrinterName(index:number,printerName: any):string{
    return printerName?.name + index;
  }

  // Function to adjust other collapse states when one is opened
  private adjustOtherCollapses(currentAction: string): void {
    for (const action in this.isCollapsed) {
      if (action !== currentAction) {
        // Close other collapse states
        this.isCollapsed[action] = false;
      }
    }
  }
}
